import { Fragment, useState } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import { EditableCell } from './EditableCell';
import { SpellersTasksCell } from './SpellersTasksCell';
import { SpellersPackagesSection } from './SpellersPackagesSection';
import { SpellersCommsRow } from './SpellersCommsRow';
import { ProjectManagementModal } from '../ProjectManagementModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_COLORS = {
  'Pre-start': 'bg-amber-100 text-amber-800 border-amber-300',
  'Tender': 'bg-orange-100 text-orange-700 border-orange-300',
  'In Progress': 'bg-blue-100 text-blue-700 border-blue-300',
  'Review': 'bg-red-100 text-red-700 border-red-300',
  'On Hold': 'bg-gray-200 text-gray-700 border-gray-300',
  'TBC': 'bg-gray-200 text-gray-700 border-gray-300',
  __default: 'bg-slate-100 text-slate-700 border-slate-300',
};

const STATUS_COLORS = {
  'Complete': 'bg-red-100 text-red-700 border-red-300',
  'On Hold': 'bg-gray-200 text-gray-700 border-gray-300',
  'Live': 'bg-emerald-100 text-emerald-700 border-emerald-300',
  __default: 'bg-slate-100 text-slate-700 border-slate-300',
};

/** Spreadsheet-style list of Speller Metcalfe projects — click a name for full detail. */
export const SpellersProjectsList = ({ projects, onUpdated, user }) => {
  const [openProjectId, setOpenProjectId] = useState(null);

  const saveField = async (project, field, value) => {
    try {
      const res = await api.put(`${API}/spellers/projects/${project.id}`, { [field]: value });
      onUpdated?.(res.data.project);
    } catch (e) {
      toast.error('Failed to save');
    }
  };

  if (projects.length === 0) {
    return <div className="text-sm text-gray-400 text-center py-12">No Spellers projects yet</div>;
  }

  return (
    <div className="overflow-x-auto rounded-2xl border-2 border-slate-300 shadow-lg bg-white" data-testid="spellers-projects-list">
      <table className="w-full text-sm table-fixed min-w-[1100px]">
        <colgroup>
          <col style={{ width: '19%' }} />
          <col style={{ width: '9%' }} />
          <col style={{ width: '10%' }} />
          <col style={{ width: '18%' }} />
          <col style={{ width: '9%' }} />
          <col style={{ width: '10%' }} />
          <col style={{ width: '8%' }} />
          <col style={{ width: '17%' }} />
        </colgroup>
        <thead>
          <tr className="bg-slate-800 text-left text-xs font-bold text-white uppercase tracking-wide">
            <th className="px-3 py-3 min-w-[220px]">Project</th>
            <th className="px-3 py-3 min-w-[110px]">Stage</th>
            <th className="px-3 py-3 min-w-[130px]">Status</th>
            <th className="px-3 py-3 min-w-[180px]">Location / Postcode</th>
            <th className="px-3 py-3 min-w-[100px]">Value</th>
            <th className="px-3 py-3 min-w-[110px]">Start Date</th>
            <th className="px-3 py-3 min-w-[90px]">Packages</th>
            <th className="px-3 py-3 min-w-[140px]">Tasks</th>
          </tr>
        </thead>
        <tbody>
          {projects.map((p, idx) => {
            const rowBg = idx % 2 === 0 ? 'bg-white' : 'bg-slate-100';
            return (
            <Fragment key={p.id}>
              <tr className="bg-orange-50 border-t-2 border-slate-300 hover:bg-orange-100 transition-colors" data-testid={`spellers-project-row-${p.id}`}>
                <td className="px-3 py-2.5">
                  <button
                    onClick={() => setOpenProjectId(p.zoho_project_id)}
                    className="text-left font-bold text-slate-900 text-[13px] hover:text-orange-600 transition-colors"
                    data-testid={`spellers-project-open-${p.id}`}
                  >
                    {p.project_name || p.project_id}
                  </button>
                </td>
                <td className="px-3 py-2.5"><EditableCell value={p.stage} onSave={(v) => saveField(p, 'stage', v)} colorMap={STAGE_COLORS} testId={`spellers-project-stage-${p.id}`} /></td>
                <td className="px-3 py-2.5"><EditableCell value={p.project_status} onSave={(v) => saveField(p, 'project_status', v)} colorMap={STATUS_COLORS} testId={`spellers-project-status-${p.id}`} /></td>
                <td className="px-3 py-2.5">
                  <div className="flex gap-1">
                    <EditableCell value={p.location} onSave={(v) => saveField(p, 'location', v)} placeholder="Location" testId={`spellers-project-location-${p.id}`} />
                    <EditableCell value={p.postcode} onSave={(v) => saveField(p, 'postcode', v)} placeholder="Postcode" testId={`spellers-project-postcode-${p.id}`} />
                  </div>
                </td>
                <td className="px-3 py-2.5"><EditableCell value={p.project_value} onSave={(v) => saveField(p, 'project_value', v)} testId={`spellers-project-value-${p.id}`} /></td>
                <td className="px-3 py-2.5"><EditableCell value={p.start_date} onSave={(v) => saveField(p, 'start_date', v)} type="date" testId={`spellers-project-start-${p.id}`} /></td>
                <td className="px-3 py-2.5">
                  <span className="text-xs font-bold text-orange-600" data-testid={`spellers-project-progress-${p.id}`}>
                    {p.package_progress}
                  </span>
                </td>
                <td className="px-3 py-2.5">
                  <SpellersTasksCell zohoProjectId={p.zoho_project_id} projectName={p.project_name} testId={`spellers-project-tasks-${p.id}`} />
                </td>
              </tr>
              <tr className={rowBg}>
                <td colSpan={8} className="p-0">
                  <div className="w-full min-w-0 max-w-full">
                    <SpellersPackagesSection project={p} onUpdated={onUpdated} />
                    <SpellersCommsRow project={p} />
                  </div>
                </td>
              </tr>
            </Fragment>
          );})}
        </tbody>
      </table>
      <ProjectManagementModal
        isOpen={!!openProjectId}
        onClose={() => setOpenProjectId(null)}
        initialProjectId={openProjectId}
        user={user}
      />
    </div>
  );
};

export default SpellersProjectsList;
